import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  AreaManagementUsageTableViewModel,
  AreaReturnSbuUsageTableViewModel,
  BaseSelectViewModel,
  BillingAreaClient,
  ChangeUsageCostCenterRequest,
  CostCenterClient,
  EmploymentClient,
  EmploymentSearchResultDto,
  FrozenZoneViewModel,
  LocationClient,
  SmartPortalProcessError,
  UsageChangeRequest,
} from '@core/api';
import { AreaManagementService } from '@core/services/area-management/area-management.service';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { CostcenterSearchFormlyFieldService } from '@core/services/costcenter-search-formly-field-service/costcenter-search-formly-field.service';
import { EmployeeSearchFormlyFieldService } from '@core/services/employee-search-formly-field-service/employee-search-formly-field.service';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { TicketCreateBaseComponent } from '@features/tickets/components/ticket-create/ticket-create.base';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { CellTemplatesComponent } from '@shared/components/cell-templates/cell-templates.component';
import { SafeHtmlPipe } from '@shared/pipes';
import { addDays, format, parse, set } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Observable,
  of,
} from 'rxjs';
import { AreaType } from '../area-return/area-return.component';

interface IAreaReturnSbuUsageTableViewModel
  extends AreaReturnSbuUsageTableViewModel {
  requestedUsageUntil: Date;
  numberOfWorkspacesSelection: number;
}

@Component({
  selector: 'app-costcenter-change',
  standalone: true,
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatFormFieldModule,
    MatDatepickerModule,
    FormlyMatDatepickerModule,
    SafeHtmlPipe,
    OverrideModule,
    CellTemplatesComponent,
  ],
  templateUrl: './costcenter-change.component.html',
  styleUrl: './costcenter-change.component.scss',
})
export class CostcenterChangeComponent
  extends TicketCreateBaseComponent
  implements OnInit, OnDestroy
{
  readonly breadcrumbsService = inject(BreadcrumbService);
  readonly locationFormlyFieldService = inject(LocationFormlyFieldService);
  readonly employeeSearchFormlyFieldService = inject(
    EmployeeSearchFormlyFieldService
  );
  readonly costCenterFormlyFieldService = inject(
    CostcenterSearchFormlyFieldService
  );
  readonly toastr = inject(ToastrService);
  readonly employmentClient = inject(EmploymentClient);
  readonly billingAreaClient = inject(BillingAreaClient);
  readonly locationClient = inject(LocationClient);
  readonly costcenterClient = inject(CostCenterClient);
  readonly areaManagementService = inject(AreaManagementService);

  @ViewChild('sharedTemplates', { static: true })
  sharedTemplates!: CellTemplatesComponent;

  @ViewChild('deleteCellTemplate', { static: true })
  deleteCellTemplate: TemplateRef<void>;

  @ViewChild('datepickerUsageUntilCellTemplate', { static: true })
  datepickerUsageUntilCellTemplate: TemplateRef<void>;

  @ViewChild('datepickerRequestedUsageUntilCellTemplate', { static: true })
  datepickerRequestedUsageUntilCellTemplate: TemplateRef<void>;

  headline: string = this.translateService.instant('costcenter_change.title');

  currentUserEmail = this.msalService.instance.getActiveAccount()?.idTokenClaims
    ?.email as string;

  currentUser: EmploymentSearchResultDto;

  changeReasons: BaseSelectViewModel[] = [];

  frozenZone: { startDate: Date; endDate: Date };
  frozenZones: FrozenZoneViewModel[];

  minDate: Date = addDays(new Date(), 1);

  isVb: boolean = false;

  locationId: string = '';

  currentLocationId: string = '';

  costcenterId: string = '';

  sbuId: string = '';

  columns: MtxGridColumn[];
  usageSelectionDataTable$: Observable<AreaManagementUsageTableViewModel[]>;
  columnsSelectedUsages: MtxGridColumn[];

  public dataUsageSelection$ = new BehaviorSubject<
    AreaManagementUsageTableViewModel[]
  >([] as AreaManagementUsageTableViewModel[]);

  selectedRowsSubject$ = new BehaviorSubject<
    AreaManagementUsageTableViewModel[]
  >([]);

  columnsVb: MtxGridColumn[];
  columnsSelectedUsagesVb: MtxGridColumn[];

  public dataVbUsageSelection$ = new BehaviorSubject<
    AreaReturnSbuUsageTableViewModel[]
  >([] as AreaReturnSbuUsageTableViewModel[]);

  vbSelectedRowsSubject$ = new BehaviorSubject<
    IAreaReturnSbuUsageTableViewModel[]
  >([]);

  billingAreasSubject$ = new BehaviorSubject<BaseSelectViewModel[]>([]);

  costcenterNew = new FormControl();

  costcenter = new FormControl();

  override redirectButtonLabel = this.translateService.instant(
    'costcenter_change.overview'
  );

  override navigationUrl = '/datamaintenance/costcenter-change-overview';

  public ngOnInit(): void {
    this.description = this.translateService.instant(
      'costcenter_change.description'
    );
    this.descriptionStyle =
      'background-color: #E20074; color: white; border-radius: 5px; padding: 15px;';
    this.descriptionContainerStyle =
      'width: 100%; max-width: 840px; border-radius: 15px; padding: 0 15px;';

    this.breadcrumbsService.setBreadcrumbs([
      {
        label: this.headline,
      },
    ]);

    this.card = {
      footer: {
        button1: {
          label: this.translateService.instant('forward'),
          disabled: false,
          onClick: () => {
            const selectedRows =
              this.selectedRowsSubject$.getValue().length > 0
                ? this.selectedRowsSubject$.getValue()
                : this.vbSelectedRowsSubject$.getValue();

            if (selectedRows.length == 0) {
              this.toastr.error(
                this.translateService.instant(
                  'costcenter_change.selected_usages.required_error'
                )
              );
              return;
            }

            const invalidDate = selectedRows
              .map(item => item.usageUntil ?? item.requestedUsageUntil)
              .every(date => this.dateFilter(date) && date >= this.minDate);

            if (!invalidDate) {
              this.toastr.error(
                this.translateService.instant(
                  'costcenter_change.error.date_general'
                )
              );
              return;
            }

            this.validateForm();
          },
        },
      },
    };

    this.successTitle = this.translateService.instant(
      'costcenter_change.success.title'
    );

    this.successMessage = this.translateService.instant(
      'costcenter_change.success.message'
    );

    this.loading = true;
    this.loadingMessage = this.translateService.instant('form.loading');

    this.columns = [
      {
        header: this.translateService.instant(
          'area_return.label__selected_usage'
        ),
        field: 'selected',
        cellTemplate: this.sharedTemplates.selectionCellTemplateRef,
      },
      {
        header: this.translateService.instant('area_return.label__costCenter'),
        field: 'costCenter',
      },
      {
        header: this.translateService.instant('area_return.label__room'),
        field: 'roomName',
      },
      {
        header: this.translateService.instant('area_return.label__workSpace'),
        field: 'workspaceName',
      },
      {
        header: this.translateService.instant(
          'area_return.label__buildingFloor'
        ),
        field: 'buildingFloorTitle',
      },
      {
        header: this.translateService.instant('area_return.label__m2'),
        field: 'area',
      },
      {
        header: this.translateService.instant('area_return.label__validUntil'),
        field: 'usageUntil',
      },
    ];

    this.columnsSelectedUsages = [
      {
        header: this.translateService.instant(
          'area_return.label__delete_usage'
        ),
        field: 'delete',
        cellTemplate: this.deleteCellTemplate,
      },
      {
        header: this.translateService.instant('area_return.label__costCenter'),
        field: 'costCenter',
      },
      {
        header: this.translateService.instant(
          'costcenter_change.label__changeDate'
        ),
        field: 'usageUntil',
        cellTemplate: this.datepickerUsageUntilCellTemplate,
      },
      {
        header: this.translateService.instant('area_return.label__room'),
        field: 'roomName',
      },
      {
        header: this.translateService.instant('area_return.label__workSpace'),
        field: 'workspaceName',
      },
      {
        header: this.translateService.instant(
          'area_return.label__buildingFloor'
        ),
        field: 'buildingFloorTitle',
      },
      {
        header: this.translateService.instant('area_return.label__m2'),
        field: 'area',
      },
      {
        header: this.translateService.instant('area_return.label__validUntil'),
        field: 'originalUsageUntil',
      },
    ];

    this.columnsVb = [
      {
        header: this.translateService.instant(
          'area_return.label__selected_usage'
        ),
        field: 'selected',
        cellTemplate: this.sharedTemplates.selectionCellTemplateRef,
      },
      {
        header: this.translateService.instant('area_return.label__costCenter'),
        field: 'costCenterId',
      },
      {
        header: this.translateService.instant(
          'costcenter_change.selected_usages.label__quantity'
        ),
        field: 'numberOfWorkspaces',
      },
      {
        header: this.translateService.instant('area_return.label__validUntil'),
        field: 'usageUntil',
        cellTemplate: this.sharedTemplates.dateCellTemplate,
      },
    ];

    this.columnsSelectedUsagesVb = [
      {
        header: this.translateService.instant(
          'area_return.label__delete_usage'
        ),
        field: 'delete',
        cellTemplate: this.deleteCellTemplate,
      },
      {
        header: this.translateService.instant(
          'costcenter_change.label__changeDate'
        ),
        field: 'requestedUsageUntil',
        cellTemplate: this.datepickerRequestedUsageUntilCellTemplate,
      },
      {
        header: this.translateService.instant('area_return.label__costCenter'),
        field: 'costCenterId',
      },
      {
        header: this.translateService.instant(
          'costcenter_change.selected_usages.label__quantity'
        ),
        field: 'numberOfWorkspaces',
      },
      {
        header: this.translateService.instant('area_return.label__validUntil'),
        field: 'usageUntil',
        cellTemplate: this.sharedTemplates.dateCellTemplate,
      },
    ];

    forkJoin({
      user: this.employmentClient.getEmployeeByEmail(this.currentUserEmail),
      frozenZone: this.locationClient.checkFrozenZone(),
      frozenZones: this.locationClient.getFrozenZones(),
      changeReasons: this.locationClient.getChangeReasons().pipe(
        catchError(() => {
          return of([]);
        })
      ),
      ccSbu: this.costcenterClient.getCostCenterForUser(),
    }).subscribe({
      next: ({ user, frozenZone, frozenZones, changeReasons, ccSbu }) => {
        this.currentUser = user;
        this.frozenZones = frozenZones;
        this.changeReasons = changeReasons;
        this.costcenterId = ccSbu?.costCenter;
        this.sbuId = ccSbu?.sbuId;
        this.costCenterFormlyFieldService.sbuId = this.sbuId;

        if (frozenZone) {
          this.frozenZone = {
            startDate: parse(frozenZone.startDate, 'dd.MM.yyyy', new Date()),
            endDate: set(parse(frozenZone.endDate, 'dd.MM.yyyy', new Date()), {
              hours: 12,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            }),
          };
          this.minDate = addDays(this.frozenZone.endDate, 1);
          this.minDate.setHours(0, 0, 0, 0);
        }

        this.formFields = this.getFormFields();
        this.loading = false;
        this.loadingMessage = this.translateService.instant(
          'form.loading_message'
        );
      },
      error: () => {
        this.loading = false;
        this.loadingMessage = this.translateService.instant(
          'form.loading_message'
        );
      },
    });

    this.areaManagementService.rowUpdated$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe(newRow => {
        if (Array.isArray(newRow)) {
          const rowDataArray = newRow
            .map(item => ({
              ...item.rowData,
              usageUntil: this.areaManagementService.getUsageUntil(
                this.form.get('desiredDate')
              ),
            }))
            .filter(data => data);
          const currentRows = this.selectedRowsSubject$.getValue();
          const updatedRows = [...currentRows, ...rowDataArray].filter(
            (item, index, self) =>
              index === self.findIndex(t => t.id === item.id)
          );
          this.selectedRowsSubject$.next(updatedRows);

          const updatedData = this.dataUsageSelection$.value.map(item => ({
            ...item,
            usageSelected: this.isUsageSelected(item),
          }));
          this.dataUsageSelection$.next(updatedData);

          this.updateSelectedUsesField();
          this.cd.detectChanges();
        }
      });

    this.areaManagementService.rowVbUpdated$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe(newRow => {
        if (Array.isArray(newRow)) {
          const rowDataArray = newRow
            .map(item => ({
              ...item.rowData,
              usageUntil: this.areaManagementService.getUsageUntil(
                this.form.get('desiredDate')
              ),
              numberOfWorkspacesSelection: item.rowData.numberOfWorkspaces,
            }))
            .filter(data => data);
          const currentRows = this.vbSelectedRowsSubject$.getValue();
          const updatedRows = [...currentRows, ...rowDataArray].filter(
            (item, index, self) =>
              index === self.findIndex(t => t.id === item.id)
          );
          this.vbSelectedRowsSubject$.next(updatedRows);

          const updatedData = this.dataVbUsageSelection$.value.map(item => ({
            ...item,
            usageSelected: this.isVbUsageSelected(item),
          }));
          this.dataVbUsageSelection$.next(updatedData);

          this.updateVbSelectedUsesField();
          this.cd.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.costCenterFormlyFieldService.sbuId = null;
    this.dataUsageSelection$.next([]);
    this.selectedRowsSubject$.next([]);
    this.dataVbUsageSelection$.next([]);
    this.vbSelectedRowsSubject$.next([]);
    this.areaManagementService.resetRows();
  }

  onDeleteSelectedUsage(row, isVb: boolean) {
    if (isVb) {
      this.areaManagementService.deleteRowVb(row.id);
    } else {
      this.areaManagementService.deleteRow(row.id);
    }

    if (isVb) {
      const rowsUpdate = this.vbSelectedRowsSubject$.value.filter(
        item => item.id !== row.id
      );
      this.vbSelectedRowsSubject$.next(rowsUpdate);

      const updatedData = this.dataVbUsageSelection$.value.map(item => ({
        ...item,
        usageSelected: this.isVbUsageSelected(item),
      }));
      this.dataVbUsageSelection$.next(updatedData);
    } else {
      const rowsUpdate = this.selectedRowsSubject$.value.filter(
        item => item.id !== row.id
      );
      this.selectedRowsSubject$.next(rowsUpdate);

      const updatedData = this.dataUsageSelection$.value.map(item => ({
        ...item,
        usageSelected: this.isUsageSelected(item),
      }));
      this.dataUsageSelection$.next(updatedData);
    }
  }

  private updatingFormFields = false;

  updateSelectedUsesField() {
    if (this.formFields) {
      if (this.updatingFormFields) {
        return;
      }

      this.updatingFormFields = true;
      const usageSelectedUses = this.formFields[0].fieldGroup.find(
        f => f.key === 'selected_uses'
      );

      const index = this.formFields?.findIndex(group =>
        group.fieldGroup?.some(field => field.key === 'selected_uses')
      );

      if (index !== -1) {
        if (this.formFields) {
          const group = this.formFields[index];
          const fieldIndex = group.fieldGroup.findIndex(
            field => field.key === 'selected_uses'
          );

          if (fieldIndex !== -1) {
            group.fieldGroup[fieldIndex] = usageSelectedUses;
            this.selectedRowsSubject$.subscribe({
              next: data => {
                this.form.value['selected_uses'] = data;
                this.cd.detectChanges();
              },
            });
            this.formFields = [...this.formFields];
          }
        }
      }

      this.updatingFormFields = false;
    }
  }

  updateVbSelectedUsesField() {
    if (this.formFields) {
      if (this.updatingFormFields) {
        return;
      }

      this.updatingFormFields = true;
      const usageSelectedUses = this.formFields[0].fieldGroup.find(
        f => f.key === 'selected_uses_vb'
      );

      const index = this.formFields?.findIndex(group =>
        group.fieldGroup?.some(field => field.key === 'selected_uses_vb')
      );

      if (index !== -1) {
        if (this.formFields) {
          const group = this.formFields[index];
          const fieldIndex = group.fieldGroup.findIndex(
            field => field.key === 'selected_uses_vb'
          );

          if (fieldIndex !== -1) {
            group.fieldGroup[fieldIndex] = usageSelectedUses;
            this.vbSelectedRowsSubject$.subscribe({
              next: data => {
                this.form.value['selected_uses_vb'] = data;
              },
            });
            this.formFields = [...this.formFields];
          }
        }
      }

      this.updatingFormFields = false;
    }
  }

  isUsageSelected(entry: AreaManagementUsageTableViewModel): boolean {
    return this.selectedRowsSubject$.value?.some(
      selectedRow => selectedRow.id === entry.id
    );
  }

  isVbUsageSelected(entry: AreaReturnSbuUsageTableViewModel): boolean {
    return this.vbSelectedRowsSubject$.value?.some(
      selectedRow => selectedRow.id === entry.id
    );
  }

  openCostcenterchangesList() {
    this.router.navigateByUrl(this.navigationUrl);
  }

  onSelectCostcenter(id: string) {
    this.costcenter.setValue(id);
  }

  onDeleteCostcenter() {
    this.costcenter.reset();
    this.selectedRowsSubject$.next([]);
    this.dataUsageSelection$.next([]);
  }

  onSelectCostcenterNew(id: string) {
    this.costcenterNew.setValue(id);
  }

  onDeleteCostcenterNew() {
    this.costcenterNew.reset();
  }

  onSelectLocation(id: string) {
    if (id !== this.locationId) {
      const fieldBillingArea = this.formFields[0].fieldGroup.find(
        f => f.key === 'billingArea'
      );

      this.dataVbUsageSelection$.next([]);
      this.vbSelectedRowsSubject$.next([]);
      fieldBillingArea.formControl.reset();

      if (this.form.value['areaType'] === AreaType.BillingArea) {
        this.loading = true;
        this.loadingMessage = '';
        this.locationClient.getBillingAreas(id).subscribe({
          next: billingAreas => {
            this.billingAreasSubject$.next(billingAreas);
            fieldBillingArea.props.options = billingAreas.map(
              (type: BaseSelectViewModel) => ({
                value: type.id,
                label: type.name,
              })
            );
            this.cd.detectChanges();
            this.loading = false;
            this.loadingMessage = this.translateService.instant(
              'form.loading_message'
            );
          },
        });
      }
    }
  }

  onDeleteLocation() {
    this.selectedRowsSubject$.next([]);
    this.dataUsageSelection$.next([]);
    this.dataVbUsageSelection$.next([]);
    this.vbSelectedRowsSubject$.next([]);
    this.billingAreasSubject$.next(null);
    const fieldBillingArea = this.formFields[0].fieldGroup.find(
      f => f.key === 'billingArea'
    );
    fieldBillingArea.props.options = null;
    fieldBillingArea.formControl.reset();
  }

  onModelChange(model: any) {
    if (model.searchResult !== null) {
      this.locationId = model.searchResult?.id;
    }

    this.onSelectionChange();
  }

  onSelectionChange() {
    if (!this.locationId) return;

    this.currentLocationId = this.locationId;

    if (this.form.value['areaType'] === AreaType.WorkplaceOrArea) {
      if (!this.costcenter.value) {
        return;
      }
      this.areaManagementService.loadingDataSubject$.next(true);
      this.getUsageSelectionData(this.locationId);
    } else {
      this.billingAreasSubject$.next([]);
    }
  }

  updateUsageSelectionField() {
    if (this.updatingFormFields) {
      return;
    }

    this.updatingFormFields = true;
    const usageSelectionField = this.formFields[0].fieldGroup.find(
      f => f.key === 'usage_selection'
    );

    const index = this.formFields.findIndex(group =>
      group.fieldGroup?.some(field => field.key === 'usage_selection')
    );

    if (index !== -1) {
      const group = this.formFields[index];
      const fieldIndex = group.fieldGroup.findIndex(
        field => field.key === 'usage_selection'
      );

      if (fieldIndex !== -1) {
        group.fieldGroup[fieldIndex] = usageSelectionField;
        this.formFields = [...this.formFields];
      }
    }

    this.areaManagementService.loadingDataSubject$.next(false);
    this.updatingFormFields = false;
  }

  getFormFields() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            type: 'chapter',
            props: {
              chapterTitle: this.translateService.instant(
                'costcenter_change.label__location'
              ),
            },
          },
          ...this.locationFormlyFieldService.autocompleteSearch(
            false,
            null,
            true,
            id => this.onSelectLocation(id),
            null,
            this.onDeleteLocation.bind(this),
            this.translateService.instant(
              'location.shared.delete_location_search_tooltip'
            )
          ),
          {
            type: 'select',
            key: 'areaType',
            props: {
              label: this.translateService.instant(
                'area_return.label__area_type'
              ),
              required: true,
              options: [
                {
                  value: AreaType.WorkplaceOrArea,
                  label: AreaType.WorkplaceOrArea,
                },
                {
                  value: AreaType.BillingArea,
                  label: AreaType.BillingArea,
                },
              ],
            },
            hooks: {
              onInit: field => {
                field?.formControl?.valueChanges.subscribe({
                  next: value => {
                    if (value === AreaType.BillingArea) {
                      this.isVb = true;
                      this.onDeleteCostcenter();
                      if (this.locationId) {
                        this.locationClient
                          .getBillingAreas(this.locationId)
                          .subscribe({
                            next: billingAreas => {
                              this.billingAreasSubject$.next(billingAreas);
                              const fieldBillingArea =
                                this.formFields[0].fieldGroup.find(
                                  f => f.key === 'billingArea'
                                );
                              fieldBillingArea.props.options = billingAreas.map(
                                (type: BaseSelectViewModel) => ({
                                  value: type.id,
                                  label: type.name,
                                })
                              );
                              this.cd.detectChanges();
                              this.loading = false;
                            },
                          });
                      }
                    } else {
                      this.isVb = false;
                      if (this.locationId && this.costcenter.value) {
                        this.areaManagementService.loadingDataSubject$.next(
                          true
                        );
                        this.getUsageSelectionData(this.locationId);
                      }
                    }
                  },
                });
              },
            },
          },
          {
            key: 'costcenter',
            hideExpression: () => this.isVb,
            fieldGroup: [
              ...this.costCenterFormlyFieldService.autocompleteSearch(
                this.translateService.instant(
                  'access_orderer.filter.costcenter.title'
                ) + ' *',
                true,
                null,
                '',
                id => this.onSelectCostcenter(id),
                this.onDeleteCostcenter.bind(this),
                true,
                this.translateService.instant(
                  'location.autocomplete.deleteCostCenter_tooltip'
                )
              ),
            ],
          },
          {
            type: 'select',
            key: 'billingArea',
            hideExpression: () => !this.isVb,
            props: {
              label: this.translateService.instant(
                'area_return.label__billingArea'
              ),
              options: [],
              required: true,
            },
            hooks: {
              onInit: field => {
                field?.formControl?.valueChanges.subscribe({
                  next: value => {
                    if (value) {
                      this.billingAreaClient
                        .getSbuUsagesForChange(value)
                        .subscribe({
                          next: res => {
                            this.dataVbUsageSelection$.next(res);
                            this.vbSelectedRowsSubject$.next([]);
                            this.loading = false;
                          },
                        });
                    }
                  },
                });
              },
            },
          },
          {
            type: 'datepicker',
            key: 'desiredDate',
            className: 'description-margin',
            props: {
              label: this.translateService.instant(
                'area_return.label__desired_date'
              ),
              description: this.frozenZone
                ? this.translateService.instant(
                    'costcenter_change.description__desired_date',
                    {
                      start: format(this.frozenZone.startDate, 'dd.MM.yyyy'),
                      end: format(this.frozenZone.endDate, 'dd.MM.yyyy'),
                    }
                  )
                : null,
              datepickerOptions: {
                filter: (date: Date): boolean => this.dateFilter(date),
                min: this.minDate,
              },
              required: true,
            },
            validators: {
              customDateFilter: {
                expression: (control: FormControl) => {
                  const date =
                    control.value instanceof Date
                      ? control.value
                      : new Date(control.value);

                  const isValidDate =
                    (!this.minDate || date >= this.minDate) &&
                    this.dateFilter(date);

                  return isValidDate;
                },
                message: this.translateService.instant(
                  'costcenter_change.error__desired_date'
                ),
              },
            },
          },
          {
            key: 'usage_selection',
            type: 'usage-selection',
            hideExpression: () => this.isVb,
            props: {
              columns: this.columns,
              data$: this.dataUsageSelection$,
            },
          },
          {
            key: 'selected_uses',
            type: 'selected-uses',
            className: 'no-result-styling',
            hideExpression: () => this.isVb,
            props: {
              columns: this.columnsSelectedUsages,
              data$: this.selectedRowsSubject$,
              attributes: {
                headerStyle: 'color: black;',
              },
              noResultsText: this.translateService.instant(
                'costcenter_change.selected_usages.no_results'
              ),
            },
          },
          {
            key: 'usage_selection_vb',
            type: 'usage-selection',
            hideExpression: () => !this.isVb,
            props: {
              columns: this.columnsVb,
              data$: this.dataVbUsageSelection$,
              isVb: true,
            },
          },
          {
            key: 'selected_uses_vb',
            type: 'selected-uses',
            className: 'no-result-styling',
            hideExpression: () => !this.isVb,
            props: {
              columns: this.columnsSelectedUsagesVb,
              data$: this.vbSelectedRowsSubject$,
              attributes: {
                headerStyle: 'color: black;',
              },
              noResultsText: this.translateService.instant(
                'costcenter_change.selected_usages.no_results'
              ),
            },
          },
          {
            type: 'chapter',
            props: {
              chapterTitle: this.translateService.instant(
                'costcenter_change.label__costcenter_new'
              ),
            },
          },
          {
            key: 'costcenterNew',
            fieldGroup: [
              ...this.costCenterFormlyFieldService.autocompleteSearch(
                this.translateService.instant(
                  'access_orderer.filter.costcenter.title'
                ) + ' *',
                true,
                null,
                '',
                id => this.onSelectCostcenterNew(id),
                this.onDeleteCostcenterNew.bind(this),
                true,
                this.translateService.instant(
                  'location.autocomplete.deleteCostCenter_tooltip'
                )
              ),
            ],
          },
        ],
      },
    ];
  }

  dateFilter = (d: Date | null): boolean => {
    if (!d) return false;

    const dayToCheck = new Date(d);
    dayToCheck.setHours(0, 0, 0, 0);

    for (const zone of this.frozenZones) {
      const start = this.parseDate(zone.startDate);
      const end = this.parseDate(zone.endDate);

      if (dayToCheck >= start && dayToCheck <= end) {
        return false;
      }
    }

    return true;
  };

  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('.').map(Number);
    return new Date(year, month - 1, day);
  }

  isValidDate(date: string) {
    return !isNaN(Date.parse(date));
  }

  onDateChange(row: AreaManagementUsageTableViewModel, newDate: Date) {
    const currentRows = this.selectedRowsSubject$.getValue();
    const rows = this.areaManagementService.onDateChange(
      row,
      newDate,
      currentRows
    );
    this.selectedRowsSubject$.next([...rows]);
  }

  getUsageSelectionData(locationId: string) {
    this.usageSelectionDataTable$ = this.areaManagementService
      .getCostcenterUsagesForChange(locationId, this.costcenter.value)
      .pipe(
        debounceTime(300),
        catchError(() => {
          this.areaManagementService.loadingDataSubject$.next(false);
          this.cd.detectChanges();
          return of([] as AreaManagementUsageTableViewModel[]);
        })
      );
    this.usageSelectionDataTable$.subscribe(data => {
      if (data !== this.dataUsageSelection$.value) {
        this.dataUsageSelection$.next(data);
        this.updateUsageSelectionField();

        this.cd.detectChanges();
      }
    });
  }

  public override clientEndpoint = data => {
    this.loadingMessage = this.translateService.instant('form.loading_message');
    if (this.isVb) {
      return this.billingAreaClient.changeCostCenterOfUsages(data);
    } else {
      return this.areaManagementService.changeCostCenterOfUsages(data);
    }
  };

  protected override prepareSubmit = data => {
    const usages =
      this.selectedRowsSubject$.getValue().length > 0
        ? this.selectedRowsSubject$.getValue()
        : this.vbSelectedRowsSubject$.getValue();

    const formattedUsages: UsageChangeRequest[] = usages.map(item => ({
      changeDate:
        this.isValidDate(item.usageUntil?.toString()) === true
          ? new Date(item.usageUntil)
          : this.formModel['desiredDate'],
      usageId: item.id,
    }));

    const req: ChangeUsageCostCenterRequest = {
      usages: formattedUsages,
      costCenterId: data.costcenterNew.searchResult.costCenter,
      changeReason: 'Kostenstellenänderung WMV',
    };
    return req;
  };

  public override getStatusPath() {
    return '/dashboard';
  }

  override handleError(error: any): void {
    this.loading = false;
    const errorTitle = this.translateService.instant(
      'ticket.create.error.title'
    );
    let errorMessage = this.translateService.instant(
      'costcenter_change.error.generic'
    );

    if (error.errorCode === SmartPortalProcessError.INVALID_DATE) {
      errorMessage = this.translateService.instant(
        'costcenter_change.error.invalid_date'
      );
    } else if (error.status === 404) {
      errorMessage = this.translateService.instant(
        'costcenter_change.error.not_found'
      );
    }

    this.openErrorDialog(errorTitle, errorMessage);
  }
}
